import { ReactElement, useMemo, useState } from 'react'
import { round } from 'lodash-es'
import { Stack } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { HotkeyDeleteButton, HotkeySaveButton, SaveButton } from '@ui/actions'
import { ErrorFieldBase, Form } from '@ui/forms'
import VerticalSlabEdgeDrawingScene from '../../../VerticalSlabEdgeDrawing/VerticalSlabEdgeDrawingScene'
import FormFields from '../FormFields'
import {
  applyCreateLabel,
  createLabelSchema,
  rectangularVerticalSlabSchema,
  trapezoidalVerticalSlabSchema,
  rectangularVerticalRoofSlabSchema,
  trapezoidalVerticalRoofSlabSchema,
} from './schema'

type VerticalSlabGeometryType = 'rectangular' | 'trapezoidal'

interface Props {
  handleSubmit: (slab: VerticalSlab | VerticalRoofSlab) => void
  handleDelete: () => void
  roof: VerticalSlab | VerticalRoofSlab
  isDeleting?: boolean
  isSubmitting?: boolean

  updateShape: (shape: { guid: string; shape: PolygonShape }) => void
  updateStorey: (guid: string, storey: string) => void

  storeys: string[]
  activeStorey?: string
  showCreateLabel?: boolean
}

const GeometryForm = ({
  roof,
  handleSubmit,
  handleDelete,
  isDeleting = false,
  isSubmitting,

  updateShape,
  updateStorey,

  storeys,
  activeStorey,
  showCreateLabel = false,
}: Props): ReactElement | null => {
  const [geometryType, setGeometryType] = useState<VerticalSlabGeometryType>('rectangular')

  const defaultValues = useMemo(() => {
    const shape = roof.shape
    const numPoints = shape.points.length
    if (numPoints <= 2) {
      return {
        guid: roof.guid,
        shape: shape,
        ...(showCreateLabel ? { label: '' } : {}),
      }
    }

    return {
      guid: roof.guid,
      shape: shape,
      storey: roof.storey,
      xy_decimal_points_rounding: true,
      automatic_z_calculation: true,
      isRectangular: geometryType === 'rectangular',
      isTrapezoidal: geometryType === 'trapezoidal',
      ...(showCreateLabel ? { label: '' } : {}),
    }
  }, [roof, geometryType, showCreateLabel])

  const validationSchema = useMemo(() => {
    let geometrySchema: { concat: (schema: typeof createLabelSchema) => unknown } | undefined
    // Only apply special validation for vertical slabs and vertical roof slabs with 4 points
    if (
      (roof.element_type === 'vertical_slabs' || roof.element_type === 'vertical_roof_slabs') &&
      roof.shape.points.length === 4
    ) {
      if (roof.element_type === 'vertical_slabs') {
        geometrySchema =
          geometryType === 'rectangular'
            ? rectangularVerticalSlabSchema
            : trapezoidalVerticalSlabSchema
      } else if (roof.element_type === 'vertical_roof_slabs') {
        geometrySchema =
          geometryType === 'rectangular'
            ? rectangularVerticalRoofSlabSchema
            : trapezoidalVerticalRoofSlabSchema
      }
    }

    if (!showCreateLabel) return geometrySchema
    return geometrySchema ? geometrySchema.concat(createLabelSchema) : createLabelSchema
  }, [roof.element_type, roof.shape.points.length, geometryType, showCreateLabel])

  const SubmitButton = showCreateLabel ? HotkeySaveButton : SaveButton

  return (
    <Form
      id={'GeometryForm'}
      key={roof.guid}
      defaultValues={defaultValues}
      validationSchema={validationSchema}
      onSubmit={state => {
        const shapePoints: Point[] = []

        const originalPoints = (state.shape as PolygonShape).points
        originalPoints.forEach(point => {
          const x = state.xy_decimal_points_rounding ? round(point.x, 2) : point.x
          const y = state.xy_decimal_points_rounding ? round(point.y, 2) : point.y

          const newPoint = new ImmutableVector3(x, y, point.z)

          shapePoints.push(newPoint)
        })

        const shapeObject: VerticalSlab | VerticalRoofSlab = {
          ...roof,
          guid: state.guid,
          shape: { points: shapePoints },
          storey: state.storey,
        }

        handleSubmit(showCreateLabel ? applyCreateLabel(shapeObject, state.label) : shapeObject)
      }}
      validationContext={{
        element_type: roof.element_type,
      }}
      data-cy="rectangular-shape-geometry-form"
      enableReinitialize
      preserveOnReinitialize={[
        'xy_decimal_points_rounding',
        'automatic_z_calculation',
        ...(showCreateLabel ? ['label'] : []),
      ]}
    >
      {(roof.element_type === 'vertical_slabs' || roof.element_type === 'vertical_roof_slabs') && (
        <VerticalSlabEdgeDrawingScene elementGuid={roof.guid} elementType={roof.element_type} />
      )}
      <Stack spacing={1}>
        <FormFields
          storeys={storeys}
          updateShape={updateShape}
          updateStorey={updateStorey}
          geometryType={geometryType}
          setGeometryType={setGeometryType}
          elementType={roof.element_type}
          showCreateLabel={showCreateLabel}
        />
        {activeStorey !== undefined && activeStorey !== roof.storey && (
          <ErrorFieldBase
            message="Das eingezeichnete Element ist nicht im ausgewählten Stockwerk"
            severity="warning"
          />
        )}

        <Stack direction="row" justifyContent="end" spacing={1}>
          <SubmitButton
            type="submit"
            loading={isSubmitting}
            fullWidth
            disabled={isDeleting}
            data-cy="btn-submit"
          >
            Speichern
          </SubmitButton>
          <HotkeyDeleteButton
            onClick={handleDelete}
            size="small"
            fullWidth
            loading={isDeleting}
            disabled={isSubmitting}
            data-cy="btn-delete-vertical-roof"
          >
            Löschen
          </HotkeyDeleteButton>
        </Stack>
      </Stack>
    </Form>
  )
}

export default GeometryForm
